'use client';

import { type ReactNode, useCallback, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import logTrailSavedToastClicked from '@alltrails/amplitude/events/logTrailSavedToastClicked';
import logTrailSavedToastViewed from '@alltrails/amplitude/events/logTrailSavedToastViewed';
import ModalPortal from '@alltrails/core/components/ModalPortal';
import Toast from '@alltrails/denali/components/Toast';
import type WebList from '@alltrails/types/WebList';
import { favoritesListId } from '../../utils/listUtils';

type SaveSuccessToastProps = {
  isOpen: boolean;
  lists: WebList[];
  onEdit?: () => void;
  onTimeout?: () => void;
  onUndo?: () => void;
  setIsOpen: (isOpen: boolean) => void;
  variant?: 'saved' | 'removed';
};

export default function SaveSuccessToast({ isOpen, lists, onEdit, onTimeout, onUndo, setIsOpen, variant = 'saved' }: SaveSuccessToastProps) {
  if (!isOpen || lists.length === 0) {
    return null;
  }

  return <SaveSuccessToastContent lists={lists} onEdit={onEdit} onTimeout={onTimeout} onUndo={onUndo} setIsOpen={setIsOpen} variant={variant} />;
}

function SaveSuccessToastContent({ lists, onEdit, onTimeout, onUndo, setIsOpen, variant }: Omit<SaveSuccessToastProps, 'isOpen'>) {
  const firstList = lists[0];
  const firstListName = firstList.title;
  const otherCount = lists.length - 1;

  useEffect(() => {
    if (variant === 'saved') {
      logTrailSavedToastViewed({ list_id: firstList.id, list_type: firstList.id === favoritesListId ? 'favorites' : 'custom list' });
    }
  }, [firstList.id, variant]);

  let message: ReactNode;
  if (variant === 'removed') {
    message =
      lists.length > 1 ? (
        <FormattedMessage
          defaultMessage="Removed from {listName} and {otherCount, plural, one {# other list} other {# other lists}}"
          values={{ listName: firstListName, otherCount }}
        />
      ) : (
        <FormattedMessage defaultMessage="Removed from {listName}" values={{ listName: firstListName }} />
      );
  } else {
    message =
      lists.length > 1 ? (
        <FormattedMessage
          defaultMessage="Saved to {listName} and {otherCount, plural, one {# other list} other {# other lists}}"
          values={{ listName: firstListName, otherCount }}
        />
      ) : (
        <FormattedMessage defaultMessage="Saved to {listName}" values={{ listName: firstListName }} />
      );
  }

  const action = variant === 'removed' ? onUndo : onEdit;

  const closeOnTimeout = useCallback(
    (isOpen: boolean) => {
      setIsOpen(isOpen);
      if (!isOpen) {
        onTimeout?.();
      }
    },
    [onTimeout, setIsOpen]
  );

  return (
    <ModalPortal>
      <Toast
        action={
          action
            ? () => {
                if (variant === 'saved') {
                  logTrailSavedToastClicked({ list_id: firstList.id, list_type: firstList.id === favoritesListId ? 'favorites' : 'custom list' });
                }
                setIsOpen(false);
                action();
              }
            : undefined
        }
        actionText={variant === 'removed' ? <FormattedMessage defaultMessage="Undo" /> : <FormattedMessage defaultMessage="Edit" />}
        isOpen
        message={message}
        position="alternative"
        setIsOpen={closeOnTimeout}
        type="neutral"
      />
    </ModalPortal>
  );
}
